import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Icon,
  IconButton,
  Modal,
  Radio,
  Select,
  Skeleton,
  Stack,
  Text,
  Tooltip
} from '@efast_public/fjd-component-library';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GovosApiRoutes, govosClient } from '../api/govos-api';
import { ApiMember } from '../api/govos.types';
import { useNotification } from '../shared/notification/useNotification';

interface MembersApiResponse {
  result: string;
  members: ApiMember[];
}

interface Member {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'member' | 'moderator';
}

interface ChangeOwnerModalProps {
  onClose?: () => void;
  onSuccess?: () => void;
  isOpen: boolean;
  startAppId: number;
}

type PreviousOwnerRole = 'remove' | 'member' | 'moderator';

export function ChangeOwnerModal({
  onClose,
  onSuccess,
  isOpen,
  startAppId
}: ChangeOwnerModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUserFromRequest } = useNotification();
  const [previousOwnerRole, setPreviousOwnerRole] =
    useState<PreviousOwnerRole>('member');
  const [newOwnerId, setNewOwnerId] = useState<string>('');

  const {
    data: members = [],
    isLoading,
    refetch
  } = useQuery<Member[]>({
    queryKey: [GovosApiRoutes.members(startAppId)],
    queryFn: async () => {
      const response = await govosClient.get<MembersApiResponse>(
        GovosApiRoutes.members(startAppId)
      );
      const data = response.data;
      if (data?.result === 'success' && Array.isArray(data.members)) {
        const result: Member[] = data.members.map((member: ApiMember) => {
          let role: 'owner' | 'member' | 'moderator' = 'member';
          if (member.role === 6) {
            role = 'owner';
          } else if (member.role === 5) {
            role = 'moderator';
          } else if (member.role === 4) {
            role = 'member';
          }
          return {
            id: String(member.userid),
            name: member.dispname,
            email: member.email,
            role
          };
        });
        return result.sort((a, b) => {
          const roleOrder = { owner: 0, moderator: 1, member: 2 };
          return roleOrder[a.role] - roleOrder[b.role];
        });
      }
      return [];
    },
    enabled: isOpen && startAppId > 0
  });

  React.useEffect(() => {
    if (isOpen && startAppId > 0) {
      refetch();
    }
  }, [isOpen, startAppId, refetch]);

  const owner = members.find((m: Member) => m.role === 'owner');
  const eligibleMembers = members.filter((m: Member) => m.role !== 'owner');

  const { mutate: changeOwner, isPending } = useMutation({
    mutationFn: async () =>
      notifyUserFromRequest(
        () =>
          govosClient.post(GovosApiRoutes.changeOwner(startAppId), {
            newOwnerId,
            previousOwnerRole
          }),
        {
          successMessage: t('ChangeOwnerModal.successMessage'),
          errorMessage: t('ChangeOwnerModal.errorMessage'),
          loadingMessage: t('ChangeOwnerModal.loadingMessage')
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GovosApiRoutes.members(startAppId)]
      });
      onSuccess?.();
    }
  });

  const handleClose = () => {
    setPreviousOwnerRole('member');
    setNewOwnerId('');
    onClose?.();
  };

  if (isLoading) {
    return (
      <Modal open={isOpen} onClose={handleClose}>
        <Modal.Header id="modal-header">
          <Text size="xl" strong>
            {t('ChangeOwnerModal.title')}
          </Text>
        </Modal.Header>
        <Modal.Content>
          <Stack spacing="l">
            <Skeleton height={24} />
            <Stack spacing="s" orientation="vertical">
              <Skeleton height={20} />
              <Skeleton height={20} />
              <Skeleton height={20} />
            </Stack>
            <Skeleton height={40} />
          </Stack>
        </Modal.Content>
      </Modal>
    );
  }

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('ChangeOwnerModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <Stack spacing="l">
          <Text>
            <Text strong>{t('ChangeOwnerModal.currentOwnerLabel')} </Text>
            {owner?.name ?? '-'}
          </Text>

          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}
            >
              <Text>{t('ChangeOwnerModal.previousOwnerRoleLabel')}</Text>
              <Tooltip
                tooltip={t(
                  'ZustaendigeStelle.memberManagement.roleDescriptions'
                )}
              >
                <IconButton
                  variant="primary-text"
                  label=""
                  icon="information-filled"
                  size="sm"
                />
              </Tooltip>
            </div>
            <Stack spacing="s" orientation="vertical">
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Icon name="trashcan" size="sm" />
                <Radio
                  name="previousOwnerRole"
                  value="remove"
                  checked={previousOwnerRole === 'remove'}
                  onChange={() => setPreviousOwnerRole('remove')}
                  label={t('ChangeOwnerModal.previousOwnerRole.remove')}
                />
              </div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Icon name="user" size="sm" />
                <Radio
                  name="previousOwnerRole"
                  value="member"
                  checked={previousOwnerRole === 'member'}
                  onChange={() => setPreviousOwnerRole('member')}
                  label={t('ChangeOwnerModal.previousOwnerRole.member')}
                />
              </div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Icon name="user-role" size="sm" />
                <Radio
                  name="previousOwnerRole"
                  value="moderator"
                  checked={previousOwnerRole === 'moderator'}
                  onChange={() => setPreviousOwnerRole('moderator')}
                  label={t('ChangeOwnerModal.previousOwnerRole.moderator')}
                />
              </div>
            </Stack>
          </div>

          <Select
            label={t('ChangeOwnerModal.newOwnerLabel')}
            value={newOwnerId}
            onChange={(value) => setNewOwnerId(value.at(0)?.value ?? '')}
            options={eligibleMembers.map((m: Member) => ({
              value: m.id,
              label: `${m.name} (${m.email})`
            }))}
            placeholder={t('ChangeOwnerModal.newOwnerPlaceholder')}
          />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%'
          }}
        >
          <Button
            variant="primary"
            onClick={() => changeOwner()}
            disabled={!newOwnerId || isPending}
            style={{ width: '100%' }}
          >
            {t('ChangeOwnerModal.actions.confirm')}
          </Button>
          <Button
            variant="secondary-outline"
            onClick={handleClose}
            style={{ width: '100%' }}
          >
            {t('ChangeOwnerModal.actions.cancel')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
